import announcementPostedCue from '../assets/sounds/announcementPosted.mp3';
import confettiCue from '../assets/sounds/CanCan.mp3';
import fireworksCue from '../assets/sounds/fireworksMusic.mp3';

export const soundOptions = [
  { id: 'alert', name: 'Alert!' },
  { id: 'downer', name: 'Downer' },
  { id: 'losing', name: 'Losing' },
  { id: 'intense', name: 'Intense!' },
  { id: 'ding', name: 'Ding' },
  { id: 'bell', name: 'Bell' },
  { id: 'cheeringCrowd', name: 'Cheering Crowd' },
  { id: 'crickets', name: 'Crickets' },
  { id: 'shovelBonk', name: 'Shovel Bonk' },
  { id: 'fbiOpenUp', name: 'FBI Open Up!' },
  { id: 'success', name: 'Success!' },
  { id: 'upper', name: 'Upper' },
  { id: 'sadViolin', name: 'Sad Violin' },
  { id: 'angelicChoir', name: 'Angelic Choir' },
  { id: 'lightSaber', name: 'Light Saber' },
  { id: 'magic', name: 'Magic' },
  { id: 'ohhCrowd', name: 'Ohhhh!!!' },
  { id: 'policeSiren', name: 'Police Siren' },
  { id: 'powerUp', name: 'Power Up' },
  { id: 'doink', name: 'Doink' },
  { id: 'slap', name: 'Slap' },
  { id: 'sneaky', name: 'Sneaky' },
  { id: 'yes', name: 'Yes!!' },
  { id: 'yuck', name: 'Yuck' },
  { id: 'badumTiss', name: 'Ba Dum Tiss' },
  { id: 'gasp', name: 'Gasp!' },
  { id: 'bruh', name: 'Bruh' },
  { id: 'clapping', name: 'Clapping' },
  { id: 'crash', name: 'Crash' },
  { id: 'blueDanubeWaltz', name: 'Blue Danube Waltz' },
  { id: 'dramaticEffect', name: 'Dramatic Effect' },
  { id: 'drumRollTada', name: 'Drum Roll -> Tada' },
  { id: 'dunDunDUN', name: 'Dun, DUN, DUN!!' },
  { id: 'evilLaugh', name: 'Evil Laugh' },
  { id: 'pewPew', name: 'Pew Pew' },
  { id: 'fallingBomb', name: 'Falling Bomb' },
  { id: 'laughingCrowd', name: 'Laughing 1' },
  { id: 'laughingCrowd2', name: 'Laughing 2' },
  { id: 'pacManDying', name: 'Pac Man Dying' },
  { id: 'wompwomp', name: 'Womp Womp' },
  { id: 'wrongBuzzer', name: 'Wrong Buzzer' },
  { id: 'johnCena', name: 'John Cena' },
] as const;

export type SoundOption = (typeof soundOptions)[number];

const rawSoundEffectSources = import.meta.glob<true, string, string>('../assets/sounds/effects/*.mp3', {
  eager: true,
  import: 'default',
});

export const soundEffectSources = Object.fromEntries(
  Object.entries(rawSoundEffectSources).map(([path, source]) => {
    const fileName = path.split('/').at(-1);

    if (!fileName) {
      throw new Error(`Unable to derive a sound effect id from ${path}.`);
    }

    return [fileName.replace(/\.mp3$/u, ''), source];
  }),
) as Record<string, string>;

export const eventCueSources = {
  announcement: announcementPostedCue,
  confetti: confettiCue,
  fireworks: fireworksCue,
} as const;

export type PendingDispatchEventRecord = {
  eventType: 'announcement' | 'celebration-confetti' | 'celebration-fireworks' | 'random-selection' | 'sound-effect';
  payloadJson: string;
};

export type ParsedPendingDispatchEvent =
  | {
      eventType: 'announcement';
      message: string;
    }
  | {
      eventType: 'celebration-confetti';
      targetParticipantId: string;
    }
  | {
      eventType: 'celebration-fireworks';
      text: string;
    }
  | {
      eventType: 'random-selection';
      roomName: string;
      roomSlug: string;
      selectedParticipantId: string;
      selectedParticipantName: string;
    }
  | {
      eventType: 'sound-effect';
      soundName: string;
    };

function parsePayload(payloadJson: string): Record<string, unknown> {
  const parsed = JSON.parse(payloadJson) as unknown;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Dispatch event payloads must be JSON objects.');
  }

  return parsed as Record<string, unknown>;
}

function readRequiredString(payload: Record<string, unknown>, fieldName: string): string {
  const value = payload[fieldName];

  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Dispatch event payload field "${fieldName}" must be a non-empty string.`);
  }

  return value;
}

export function getSoundLabel(soundName: string): string {
  return soundOptions.find((option) => option.id === soundName)?.name ?? soundName;
}

export function parsePendingDispatchEvent(event: PendingDispatchEventRecord): ParsedPendingDispatchEvent {
  const payload = parsePayload(event.payloadJson);

  switch (event.eventType) {
    case 'announcement':
      return {
        eventType: 'announcement',
        message: readRequiredString(payload, 'message'),
      };
    case 'celebration-confetti':
      return {
        eventType: 'celebration-confetti',
        targetParticipantId: readRequiredString(payload, 'targetParticipantId'),
      };
    case 'celebration-fireworks':
      return {
        eventType: 'celebration-fireworks',
        text: readRequiredString(payload, 'text'),
      };
    case 'random-selection':
      return {
        eventType: 'random-selection',
        roomName: readRequiredString(payload, 'roomName'),
        roomSlug: readRequiredString(payload, 'roomSlug'),
        selectedParticipantId: readRequiredString(payload, 'selectedParticipantId'),
        selectedParticipantName: readRequiredString(payload, 'selectedParticipantName'),
      };
    case 'sound-effect':
      return {
        eventType: 'sound-effect',
        soundName: typeof payload.soundName === 'string' ? payload.soundName.trim() : '',
      };
  }
}
